import {Component, HostListener, Input, Optional} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {TemplateVariable} from '../../froala/template-variables';
import {FORMULA_FUNCTIONS, FormulaPart, parseFormula, writeFormulaPart} from './formula-language';
import {FormulaValidationService} from '../../services/formula-validation.service';
import {QuestionnaireStateService} from '../../services/questionnaire-state.service';

export interface FormulaFragmentComponent extends Translatable {}

/** Edits function operands and conditional branches in an isolated, nestable draft. */
@Component({selector: 'app-qa-formula-fragment', templateUrl: './formula-fragment.component.html'})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class FormulaFragmentComponent {
  public titleId = '';
  @Input() part: FormulaPart;
  @Input() variables: TemplateVariable[] = [];

  /** Injects modal ownership and localized labels. */
  public constructor(public activeModal: NgbActiveModal, private validation: FormulaValidationService,
    private state: QuestionnaireStateService, @Optional() private translationService: TranslationService) {}

  /** Labels every operand according to its role. */
  public fieldLabel(index: number): string {
    if (this.part.kind === 'condition') {
      return [KEYS.EDITOR.FORMULA_WHEN, KEYS.EDITOR.FORMULA_THEN, KEYS.EDITOR.FORMULA_ELSE][index];
    }
    if (this.part.name === 'match') {
      return index === 0 ? KEYS.EDITOR.FORMULA_TEXT : KEYS.EDITOR.FORMULA_PATTERN;
    }
    return KEYS.EDITOR.FORMULA_ARGUMENT;
  }

  /** Preserves operand editor instances while their string values change. */
  public trackIndex(index: number): number { return index; }

  /** Identifies functions whose operand list may grow or shrink. */
  public variadic(): boolean {
    return !!FORMULA_FUNCTIONS.find(item => this.part.name === `Math.${item.name}`)?.variadic;
  }

  /** Explains invalid fragments without evaluating user code or changing the parent document. */
  public issue(): string {
    try {
      if (this.part.fields.some((value, index) => !value.trim() && !(this.part.statement && index === 2))) {
        return KEYS.EDITOR.FORMULA_COMPLETE_FIELDS;
      }
      parseFormula(writeFormulaPart(this.part));
      const error = this.validation.validate(writeFormulaPart(this.part), this.state.getQuestionnaire());
      if (!error) { return ''; }
      return error.type === 'circular' ? KEYS.EDITOR.INVALID_CIRCULAR_EXPRESSION :
        error.type === 'unknownVariable' ? KEYS.EDITOR.FORMULA_UNKNOWN_VARIABLE :
          error.type === 'invalidFunctionCall' ? KEYS.EDITOR.FORMULA_RUNTIME_CALL : KEYS.EDITOR.INVALID_EXPRESSION;
    } catch (_) { return KEYS.EDITOR.FORMULA_SYNTAX_ERROR; }
  }

  /** Applies only syntactically complete fragments. The containing form also checks runtime compatibility. */
  public apply(): void {
    if (!this.issue()) { this.activeModal.close(this.part); }
  }

  /** Keeps save shortcuts within this draft instead of saving the questionnaire underneath. */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault(); event.stopPropagation(); this.apply();
    }
  }
}
